import { Field, Formik } from 'formik';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { AxiosUtils, FormikUtils } from '../../shared/utils';
import {
	CompanyContact,
	CompanyContactDataRoom,
	CompanyContactDataRoomAccessTypeMap,
	CurrentUser,
} from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../shared/hooks';
import { Grid } from '@mui/material';
import { getApiResponseData } from '../../shared/utils/axios';

interface AddColleagueAccessDrawerProps {
	record: CompanyContact | null;
	companyGuid: string;
	dataRoomGuid: string;
	currentUser: CurrentUser;
	onClose: () => void;
}

export const AddColleagueAccessDrawer: React.FC<AddColleagueAccessDrawerProps> = ({
	record,
	companyGuid,
	dataRoomGuid,
	currentUser,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const passwordResetLinkMutation = useMutation(
		({}: any) =>
			AxiosUtils.getResponseData<string>(
				api.get(CerebroApiEndpoints.CompanyContacts.PasswordResetLink(companyGuid, record?.guid))
			),
		{
			onSuccess: data => {
				navigator.clipboard.writeText(data);
				snackbar.enqueueSnackbar('Link copied to clipboard.', { variant: 'success' });
			},
			onError: () => {
				snackbar.enqueueSnackbar('Unable to generate reset password link', { variant: 'error' });
			},
		}
	);

	const accessMutation = useMutation(
		async ({ values }: DataRoomAccessMutationVariables) => {
			const { data, error } = await getApiResponseData<CompanyContactDataRoom>(
				api.post(CerebroApiEndpoints.CompanyContacts.AddExistingDataRoomAccess(companyGuid), {
					guid: record?.guid,
					dataRoomGuid: dataRoomGuid,
					isBillingContact: values.isBillingContact ?? false,
					accessStatus: values.accessStatus,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.DataRoomContacts.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Colleague created and access updated.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={record != null} onClose={() => onClose()}>
			<Formik
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(accessMutation.mutate)}
				initialValues={{
					accessStatus: 'FullAccess',
					isBillingContact: record?.isBillingContact ?? false,
					sendEmail: false,
				}}
			>
				{({ isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm
						flatIndicator
						onSubmit={handleSubmit}
						sx={{ paddingRight: 3, width: { xs: '100%', md: 400 } }}
					>
						<CerebroHeading size="h2" component="h2" gutterBottom>
							Edit Access for {record?.firstName} {record?.lastName}
						</CerebroHeading>

						<CerebroLabel htmlFor="accessStatus" sx={{ marginTop: 3 }}>
							Access Level
						</CerebroLabel>
						<Field id="accessStatus" as={CerebroDropdown} name="accessStatus" disabled={isSubmitting}>
							{Object.entries(CompanyContactDataRoomAccessTypeMap).map(([value, description]) => (
								<CerebroDropdownMenuItem key={value} value={value}>
									{description}
								</CerebroDropdownMenuItem>
							))}
						</Field>

						<CerebroText variant="hint">
							<ul>
								<li>Full Access: can access and edit all sections of the loan application.</li>
								<li>
									Limited Access: can access and edit all sections of the loan application; can not
									give access to others.
								</li>
								<li>No Access: no access to the loan application.</li>
							</ul>
						</CerebroText>

						{currentUser.isEmployeeUser && (
							<>
								<CerebroLabel htmlFor="isBillingContact" sx={{ marginTop: 3 }}>
									Billing Contact
								</CerebroLabel>
								<Field
									id="isBillingContact"
									name="isBillingContact"
									as={CerebroYesNo}
									disabled={isSubmitting}
								/>

								<CerebroLabel htmlFor="sendEmail" sx={{ marginTop: 3 }}>
									Send Email
								</CerebroLabel>
								<Field id="sendEmail" name="sendEmail" as={CerebroYesNo} disabled={isSubmitting} />
							</>
						)}

						{currentUser.isEmployeeUser && (
							<>
								<Grid
									sx={{
										marginBottom: 1,
										marginTop: 3,
									}}
									display={'flex'}
								>
									<Grid item>
										<CerebroSecondaryButton onClick={passwordResetLinkMutation.mutate}>
											Copy To Clipboard
										</CerebroSecondaryButton>
									</Grid>
									<Grid item>
										<CerebroPrimaryButton
											loading={isSubmitting}
											type="submit"
											sx={{ marginLeft: 3 }}
										>
											Save Access
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</>
						)}

						{!currentUser.isEmployeeUser && (
							<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 6 }}>
								Save Access
							</CerebroPrimaryButton>
						)}
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface DataRoomAccessFormValues {
	accessStatus: string;
	isBillingContact: boolean;
	sendEmail: boolean;
}

type DataRoomAccessMutationVariables = FormikUtils.MutateOnSubmitVariables<DataRoomAccessFormValues>;
